/**
 * Tags Page
 * Manage document tags and view documents by tag
 */

import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  TagIcon,
  PlusIcon,
  XMarkIcon,
  DocumentTextIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import { motion, AnimatePresence } from 'framer-motion'
import { documentsAPI } from '@api/documents'
import toast from 'react-hot-toast'
import clsx from 'clsx'
import { formatDistanceToNow } from 'date-fns'

const TagsPage: React.FC = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [selectedTag, setSelectedTag] = useState<string | null>(null)
  const [newTagName, setNewTagName] = useState('')
  const [showCreateTag, setShowCreateTag] = useState(false)

  // Fetch all documents to extract tags
  const { data: documents = [], isLoading } = useQuery({
    queryKey: ['documents'],
    queryFn: () => documentsAPI.getAll(),
    refetchOnWindowFocus: true,
  })

  // Extract all unique tags from documents
  const allTags = React.useMemo(() => {
    const tagSet = new Set<string>()
    documents.forEach((doc: any) => {
      if (doc.tags && Array.isArray(doc.tags)) {
        doc.tags.forEach((tag: string) => tagSet.add(tag))
      }
    })
    return Array.from(tagSet).sort()
  }, [documents])

  // Get documents for selected tag
  const taggedDocuments = React.useMemo(() => {
    if (!selectedTag) return []
    return documents.filter((doc: any) => 
      doc.tags && Array.isArray(doc.tags) && doc.tags.includes(selectedTag)
    )
  }, [documents, selectedTag])

  const handleTagClick = (tag: string) => {
    setSelectedTag(tag)
  }

  const handleCreateTag = async () => {
    if (!newTagName.trim()) {
      return
    }
    
    const tagName = newTagName.trim()
    
    // Check if tag already exists
    if (allTags.includes(tagName)) {
      return
    }
    
    // Tags are created when added to documents
    // Show instructions to the user
    if (documents.length === 0) {
      setNewTagName('')
      setShowCreateTag(false)
      return
    }
    
    // Show message that tag will appear when added to a document
    setNewTagName('')
    setShowCreateTag(false)
  }

  const handleRemoveTag = async (tag: string) => {
    // Remove tag from all documents that have it
    const docsWithTag = documents.filter((doc: any) => 
      doc.tags && Array.isArray(doc.tags) && doc.tags.includes(tag)
    )
    
    // Update each document to remove the tag
    try {
      await Promise.all(
        docsWithTag.map((doc: any) => {
          const updatedTags = doc.tags.filter((t: string) => t !== tag)
          return documentsAPI.update(doc.id, { tags: updatedTags })
        })
      )
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      if (selectedTag === tag) {
        setSelectedTag(null)
      }
    } catch (error) {
    }
  }

  const handleDocumentClick = (documentId: string) => {
    navigate(`/editor/${documentId}`)
  }

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <TagIcon className="w-6 h-6" />
              Tags
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Organize your documents with tags
            </p>
          </div>
          <button
            onClick={() => setShowCreateTag(true)}
            className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Create Tag
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Tags Sidebar */}
        <div className="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
          <div className="p-4">
            <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
              All Tags ({allTags.length})
            </h2>
            
            {showCreateTag && (
              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <input
                  type="text"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleCreateTag()
                    } else if (e.key === 'Escape') {
                      setShowCreateTag(false)
                      setNewTagName('')
                    }
                  }}
                  placeholder="Tag name..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  autoFocus
                />
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={handleCreateTag}
                    className="flex-1 px-3 py-1.5 bg-primary-600 text-white rounded text-sm hover:bg-primary-700"
                  >
                    Create
                  </button>
                  <button
                    onClick={() => {
                      setShowCreateTag(false)
                      setNewTagName('')
                    }}
                    className="flex-1 px-3 py-1.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded text-sm hover:bg-gray-300 dark:hover:bg-gray-500"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {isLoading ? (
              <div className="text-center py-8">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-primary-600 border-t-transparent" />
              </div>
            ) : allTags.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <TagIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No tags yet</p>
                <p className="text-xs mt-1">Create a tag to get started</p>
              </div>
            ) : (
              <div className="space-y-1">
                {allTags.map((tag) => {
                  const docCount = documents.filter((doc: any) => 
                    doc.tags && Array.isArray(doc.tags) && doc.tags.includes(tag)
                  ).length
                  
                  return (
                    <div
                      key={tag}
                      className={clsx(
                        'group flex items-center justify-between px-3 py-2 rounded-lg cursor-pointer transition-colors',
                        selectedTag === tag
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-900 dark:text-primary-300'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                      )}
                      onClick={() => handleTagClick(tag)}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <TagIcon className="w-4 h-4 flex-shrink-0" />
                        <span className="font-medium truncate">{tag}</span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          ({docCount})
                        </span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleRemoveTag(tag)
                        }}
                        className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-opacity"
                        title="Delete tag"
                      >
                        <TrashIcon className="w-4 h-4 text-red-600 dark:text-red-400" />
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Documents List */}
        <div className="flex-1 overflow-y-auto p-6">
          {selectedTag ? (
            <>
              <div className="mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                  Documents tagged "{selectedTag}"
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {taggedDocuments.length} {taggedDocuments.length === 1 ? 'document' : 'documents'}
                </p>
              </div>

              {taggedDocuments.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  <DocumentTextIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>No documents with this tag</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {taggedDocuments.map((doc: any) => (
                    <motion.div
                      key={doc.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white dark:bg-gray-800 rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer p-4"
                      onClick={() => handleDocumentClick(doc.id)}
                    >
                      <h3 className="font-medium text-gray-900 dark:text-white mb-2 truncate">
                        {doc.title}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">
                        {doc.content?.substring(0, 100) || 'No content'}...
                      </p>
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>{doc.word_count} words</span>
                        <span>{formatDistanceToNow(new Date(doc.updated_at), { addSuffix: true })}</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <TagIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium mb-2">Select a tag to view documents</p>
              <p className="text-sm">Tags help you organize and find your documents quickly</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TagsPage

